import "./Fillout.css";
import { useEffect, useState } from "react";
import api from "../../../api/resources";
import { Content, Header, Main } from "../../commons/Template/Template";
import { Amount, CreditLineGauge } from "../Common/CreditLine/CreditLine";
import { Form, FormLoader } from "../../commons/Form/Form";
import { useAuth } from "../../../auth/AuthContext";
import { useTranslation } from "react-i18next";
import { addDays, differenceInCalendarDays, formatDate } from "date-fns";
import { formatAmount } from "../../../format";
import { i18n } from "../../../i18n";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import Button from "../../commons/Button/Button";
import {
  AddBox,
  Delete,
  Description,
  Edit,
  InfoOutlined,
} from "@mui/icons-material";
import { useCountries } from "../../../hooks/commons/useCountries";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { capitalize } from "../../../utils";
import useInvoiceCheck from "../useInvoiceCheck";
import useInvoiceType from "../useInvoiceType";
import Typo from "../../commons/Typo/Typo";

export default function Fillout({ need, usecase, patchID = 0, onEstimate }) {
  const { t } = useTranslation(["common", "financing"]);
  document.title = t("financing:fillout.title");

  // Init.
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const { activeCompany, loadingActiveCompany } = useAuth();

  const [creditLine, setCreditLine] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const initCreditLine = async () => {
    const response = await api.getUsecases(need);
    if (response.ok) {
      const jsonUsecases = await response.json();
      const currentUsecase = jsonUsecases[usecase];
      if (currentUsecase.credit_line) {
        const calcCreditLine = {
          available: currentUsecase.credit_line.available_amount,
          max: currentUsecase.credit_line.max_amount,
          currency: activeCompany.currency,
        };
        setCreditLine(calcCreditLine);
      }
    }
  };

  const initBankAccounts = async () => {
    const response = await api.getAccounts();
    if (response.ok) {
      const accounts = await response.json();
      const options = accounts
        .filter((account) => !account.closing_date)
        .map((account) => ({
          label: account.name,
          value: account.id,
        }));
      setBankAccounts(options);
    }
  };

  const initCurrencies = async () => {
    const response = await api.getCurrencies();
    if (response.ok) {
      const jsonCurrencies = await response.json();
      const currenciesOptions = jsonCurrencies.map((currency) => ({
        label: currency,
        value: currency,
      }));
      setCurrencies(currenciesOptions);
      const activeCompanyCurrency = currenciesOptions.find(
        (currency) => currency.value === activeCompany.currency,
      );
      handleLoanRequestChange(activeCompanyCurrency, "currency");
    }
  };

  // --- DATES

  const today = () => new Date().setHours(0, 0, 0, 0);
  const formatDateForInput = (date) => formatDate(date, "yyyy-MM-dd");
  const [days, setDays] = useState("");

  const maxDays = 90;

  const isDateValid = (dateStr) => !isNaN(new Date(dateStr));

  // if date is valid, format it, else set it to a default (today)
  const setDateInput = (inputValue) =>
    formatDateForInput(
      inputValue && isDateValid(inputValue) ? inputValue : today(),
    );

  const handleDateChange = (value, setFn) =>
    value && isDateValid(value) ? setFn(value) : setFn(today());

  // --- END DATES

  // Results

  const initialLoanRequestErrors = {
    need: [],
    use_case: [],
    starting_date: [],
    ending_date: [],
    amount: [],
    currency: [],
    invoice: [],
    bank_account: [],
    months: [],
    default_reasons: [],
    custom_reason: [],
    non_field_errors: [],
  };

  const [loanRequest, setLoanRequest] = useState({
    id: patchID,
    need: need,
    use_case: usecase,
    starting_date: addDays(today(), usecase === "multi_invoice" ? 3 : 1),
    ending_date: addDays(today(), 2),
    amount: "",
    currency: null,
    invoices: [], // array of IDs
    bank_account: null,
    months: 0,
    default_reasons: [],
    custom_reason: "",
  });

  const handleLoanRequestChange = (newValue, field) => {
    setLoanRequest((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const [loanRequestErrors, setLoanRequestErrors] = useState(
    initialLoanRequestErrors,
  );

  const handleErrorsChange = (newErrors = {}) => {
    setLoanRequestErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));
  };

  const clearErrors = () => {
    setLoanRequestErrors(initialLoanRequestErrors);
  };

  const [invoiceType] = useState(
    need === "long_term" || need === "short_term" ? null : useInvoiceType(need),
  );
  const [invoiceFiles, setInvoiceFiles] = useState([]);

  const [page, setPage] = useState("");

  // Page 1: create invoice + create loan request
  const creditDateInput = {
    type: "date",
    name: "creditDate",
    label: t("common:creditDate"),
    value: setDateInput(loanRequest.starting_date),
    onChange: (value) =>
      handleDateChange(value, () =>
        handleLoanRequestChange(value, "starting_date"),
      ),
    errors: loanRequestErrors.starting_date,
    min: formatDateForInput(addDays(today(), 1)),
  };

  const refundDateInput = {
    type: "date",
    name: "refundDate",
    label: t("common:refundDate"),
    value: setDateInput(loanRequest.ending_date),
    onChange: (value) =>
      handleDateChange(value, () =>
        handleLoanRequestChange(value, "ending_date"),
      ),
    errors: loanRequestErrors.ending_date,
    min: formatDateForInput(
      addDays(setDateInput(loanRequest.starting_date), 1),
    ),
    max: formatDateForInput(
      addDays(setDateInput(loanRequest.starting_date), maxDays),
    ),
  };

  const handleDaysChange = (newDays) => {
    // set days
    setDays(newDays);

    // compute ending day accordingly
    if (newDays && newDays <= maxDays) {
      const newEndingDate = addDays(
        loanRequest.starting_date,
        parseInt(newDays),
      );
      handleLoanRequestChange(newEndingDate, "ending_date");
    }
  };

  const daysInput = {
    type: "number",
    name: "days",
    label: capitalize(t("common:days")),
    value: days,
    onChange: (value) => handleDaysChange(value),
    min: 1,
    max: maxDays,
  };

  // Compute (days) difference between refund and credit dates
  useEffect(() => {
    if (loanRequest.starting_date && loanRequest.ending_date) {
      setDays(
        differenceInCalendarDays(
          loanRequest.ending_date,
          loanRequest.starting_date,
        ),
      );
    }
  }, [loanRequest.starting_date, loanRequest.ending_date]);

  const amountInputVariableCurrency = {
    type: "number",
    name: "amount",
    label: t("financing:fillout.loanRequest.form.inputs.requestedAmount"),
    value: loanRequest.amount,
    onChange: (value) => handleLoanRequestChange(value, "amount"),
    errors: loanRequestErrors.amount,
    min: 0,
    onFormatNumber: (value) =>
      formatAmount(
        value,
        i18n.resolvedLanguage,
        loanRequest.currency && loanRequest.currency.value,
        false,
        false,
      ),
  };

  const amountInputCompanyCurrency = {
    type: "number",
    name: "amount",
    label: t("financing:fillout.loanRequest.form.inputs.requestedAmount"),
    value: loanRequest.amount,
    onChange: (value) => handleLoanRequestChange(value, "amount"),
    errors: loanRequestErrors.amount,
    min: 0,
    onFormatNumber: (value) =>
      formatAmount(
        value,
        i18n.resolvedLanguage,
        loanRequest.currency?.value,
        false,
        false,
      ),
  };

  const currencyInput = {
    type: "select",
    name: "currency",
    label: t("common:currency"),
    value: loanRequest.currency,
    onChange: (value) => handleLoanRequestChange(value, "currency"),
    options: currencies,
    errors: loanRequestErrors.currency,
  };

  const durationInput = {
    type: "number",
    name: "months",
    label: `${t("common:desiredDuration")} (${t("common:inMonths")})`,
    value: loanRequest.months,
    onChange: (value) => handleLoanRequestChange(value, "months"),
    errors: loanRequestErrors.months,
    min: 1,
    max: 12,
    step: 1,
  };

  const reasonOptions = Object.entries(
    t("financing:fillout.loanRequest.form.inputs.loanRequestReasons.options", {
      returnObjects: true,
    }),
  ).map(([i18nKey, stringValue]) => ({
    label: stringValue,
    value: i18nKey,
  }));

  const reasonsInput = {
    type: "select",
    name: "reasons",
    label: t(
      "financing:fillout.loanRequest.form.inputs.loanRequestReasons.label",
    ),
    value: loanRequest.default_reasons,
    onChange: (value) => handleLoanRequestChange(value, "default_reasons"),
    options: reasonOptions,
    autoSortOptionsDisabled: true,
    isMulti: true,
    errors: loanRequestErrors.default_reasons,
  };

  const reasonELSEInput = {
    type: "text",
    name: "reasonElse",
    label: t(
      "financing:fillout.loanRequest.form.inputs.loanRequestReasonELSE.label",
    ),
    value: loanRequest.custom_reason,
    onChange: (value) => handleLoanRequestChange(value, "custom_reason"),
    maxLength: 100,
    errors: loanRequestErrors.custom_reason,
  };

  const bankAccountInput = {
    type: "select",
    name: "bankAccount",
    label: t("common:bankAccount"),
    value: loanRequest.bank_account,
    onChange: (value) => handleLoanRequestChange(value, "bank_account"),
    options: bankAccounts,
    errors: loanRequestErrors.bank_account,
  };

  const [daysSelect, setDaysSelect] = useState(null);

  const daysSelectValues = [60, 90];
  const daysSelectLabel = (value) => `${value} ${t("common:days")}`;
  const daysSelectOptions = daysSelectValues.map((value) => ({
    label: daysSelectLabel(value),
    value: value,
  }));

  const daysSelectInput = {
    type: "select",
    name: "daysSelect",
    label: t("common:duration"),
    value: daysSelect,
    onChange: setDaysSelect,
    options: daysSelectOptions,
    autoSortOptionsDisabled: true,
  };

  const creditDateSimpleInput = {
    type: "date",
    name: "creditDate",
    label: t("common:creditDate"),
    value: setDateInput(loanRequest.starting_date),
    onChange: (value) => handleLoanRequestChange(value, "starting_date"),
    errors: loanRequestErrors.starting_date,
    min: formatDateForInput(addDays(today(), 3)),
  };

  const invoiceFileInput = (nbFiles = 1) => ({
    type: "file",
    name: "invoiceFile",
    label: t("common:invoice"),
    value: invoiceFiles,
    onChange: setInvoiceFiles,
    nbFiles: nbFiles,
    errors: [],
  });

  const convertDataForAPI = (append = {}) => ({
    starting_date: formatDateForInput(loanRequest.starting_date),
    ending_date:
      loanRequest.ending_date && !loanRequest.months
        ? formatDateForInput(loanRequest.ending_date)
        : null,
    currency: loanRequest.currency.value,
    bank_account: loanRequest.bank_account
      ? loanRequest.bank_account.value
      : null,
    months: loanRequest.months || null,
    default_reasons: loanRequest.default_reasons.map((reason) => reason.value),
    // send custom_reason only if default_reasons includes "ELSE"
    custom_reason: loanRequest.default_reasons.some(
      (reason) => reason.value === "ELSE",
    )
      ? loanRequest.custom_reason
      : null,
    ...append,
  });

  const handleCreate = async () => {
    clearErrors();
    const response = await api.createLoanRequest({
      ...loanRequest,
      ...convertDataForAPI(),
    });

    if (response.ok) {
      const jsonResponse = await response.json();
      handleLoanRequestChange(jsonResponse.id, "id");
      onEstimate(jsonResponse.id);
    } else {
      const errorsJsonRes = await response.json();
      handleErrorsChange(errorsJsonRes);
    }
  };

  const handlePatch = async (onSuccess = () => onEstimate(loanRequest.id)) => {
    clearErrors();
    const loanRequestResponse = await api.patchLoanRequest(loanRequest.id, {
      ...loanRequest,
      ...convertDataForAPI(),
    });
    if (loanRequestResponse.ok) {
      onSuccess();
    } else {
      const errorsJsonRes = await loanRequestResponse.json();
      handleErrorsChange(errorsJsonRes);
    }
  };

  const handleCreateSTI = async () => {
    const handleCreateLoanRequest = async (invoiceIDs = []) => {
      const response = await api.createLoanRequest({
        ...loanRequest,
        invoices: invoiceIDs,
        ...convertDataForAPI(),
      });

      if (response.ok) {
        const jsonResponse = await response.json();
        handleLoanRequestChange(jsonResponse.id, "id");
        handleLoanRequestChange(jsonResponse.invoices, "invoices");
        setPage(2);
      } else {
        const errorsJsonRes = await response.json();
        handleErrorsChange(errorsJsonRes);
      }
    };

    clearErrors();
    const newInvoice = new FormData();
    newInvoice.append("file", invoiceFiles[0]);
    newInvoice.append("type", invoiceType.value);
    const invoiceResponse = await api.createInvoice(newInvoice);

    if (invoiceResponse.ok) {
      const jsonInvoice = await invoiceResponse.json();
      handleCreateLoanRequest([jsonInvoice.id]);
    } else {
      const errorsJsonRes = await invoiceResponse.json();
      console.log(errorsJsonRes);
    }
  };

  const handlePatchSTI = async () => {
    handlePatch(() => setPage(2));
  };

  const handleCreateMI = async () => {
    const handleCreateInvoices = async () => {
      let newInvoiceIDs = [];
      for (const invoiceFile of invoiceFiles) {
        const invoiceData = new FormData();
        invoiceData.append("file", invoiceFile);
        invoiceData.append("type", invoiceType.value);
        const invoiceResponse = await api.createInvoice(invoiceData);
        if (invoiceResponse.ok) {
          const jsonInvoice = await invoiceResponse.json();
          newInvoiceIDs.push(jsonInvoice.id);
        }
      }

      return newInvoiceIDs;
    };

    const handleCreateLoanRequest = async (invoiceIDs) => {
      const loanRequestData = {
        ...loanRequest,
        ...convertDataForAPI({
          ending_date: formatDateForInput(
            addDays(loanRequest.starting_date, daysSelect.value),
          ),
          invoices: invoiceIDs,
        }),
      };

      const response = await api.createLoanRequest(loanRequestData);
      if (response.ok) {
        const jsonResponse = await response.json();
        handleLoanRequestChange(jsonResponse.id, "id");
        handleLoanRequestChange(jsonResponse.invoices, "invoices");
        setPage(2);
      } else {
        const errorsJsonRes = await response.json();
        handleErrorsChange(errorsJsonRes);
      }
    };

    clearErrors();
    const newInvoiceIDs = await handleCreateInvoices();
    await handleCreateLoanRequest(newInvoiceIDs);
  };

  const handlePatchMI = async () => {
    const loanRequestData = {
      ...loanRequest,
      ...convertDataForAPI({
        ending_date: formatDateForInput(
          addDays(loanRequest.starting_date, daysSelect.value),
        ),
      }),
    };

    const response = await api.patchLoanRequest(
      loanRequest.id,
      loanRequestData,
    );
    if (response.ok) {
      setPage(2);
    }
  };

  const forms = [
    {
      usecase: "short_term_invoice",
      inputs: [
        {
          data: [amountInputVariableCurrency, currencyInput],
          display: "grid",
          gridTemplateColumns: "4fr 1fr",
        },
        {
          data: [creditDateInput, daysInput, refundDateInput],
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
        },
        {
          data: [bankAccountInput],
        },
        {
          data: [invoiceFileInput()],
        },
      ],
      inputsPatch: [
        {
          data: [amountInputVariableCurrency, currencyInput],
          display: "grid",
          gridTemplateColumns: "4fr 1fr",
        },
        {
          data: [creditDateInput, daysInput, refundDateInput],
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
        },
        {
          data: [bankAccountInput],
        },
      ],
      onCreate: handleCreateSTI,
      onPatch: handlePatchSTI,
    },
    {
      usecase: "treasury_tickets",
      inputs: [
        {
          data: [amountInputCompanyCurrency],
        },
        {
          data: [creditDateInput, durationInput],
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
        },
        {
          data: [bankAccountInput],
        },
        {
          data: [reasonsInput],
        },
        {
          data:
            loanRequest.default_reasons.length > 0 &&
            loanRequest.default_reasons.some(
              (reason) => reason.value === "ELSE",
            )
              ? [reasonELSEInput]
              : [],
        },
      ],
      inputsPatch: [],
      onCreate: handleCreate,
      onPatch: handlePatch,
    },
    {
      usecase: "multi_invoice",
      inputs: [
        {
          data: [creditDateSimpleInput, daysSelectInput],
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
        },
        {
          data: [invoiceFileInput(10)],
        },
      ],
      inputsPatch: [
        {
          data: [creditDateInput],
        },
        {
          data: [daysSelectInput],
        },
      ],
      onCreate: handleCreateMI,
      onPatch: handlePatchMI,
    },
  ];

  const filterForm = () => {
    const form = forms.find((form) => form.usecase === usecase);

    return {
      inputs:
        loanRequest.id && form.inputsPatch.length > 0
          ? form.inputsPatch
          : form.inputs,
      onSubmit: loanRequest.id ? form.onPatch : form.onCreate,
    };
  };

  // Page 2: patch created invoice based on created loan request

  const initData = async () => {
    const loanRequestResponse = await api.getLoanRequest(loanRequest.id);

    if (loanRequestResponse.ok) {
      const jsonLoanRequest = await loanRequestResponse.json();
      let loadedLoanRequest = {
        ...jsonLoanRequest,
        currency: currencies.find(
          (currency) => currency.value === jsonLoanRequest.currency,
        ),
        bank_account: bankAccounts.find(
          (bankAccount) => bankAccount.value === jsonLoanRequest.bank_account,
        ),
        default_reasons:
          jsonLoanRequest.default_reasons &&
          jsonLoanRequest.default_reasons.length > 0
            ? jsonLoanRequest.default_reasons.map((reasonValue) =>
                reasonOptions.find(
                  (reasonOption) => reasonOption.value === reasonValue,
                ),
              )
            : [],
      };

      Object.entries(loadedLoanRequest).forEach((entry) => {
        const field = entry[0];
        const newValue = entry[1];
        handleLoanRequestChange(newValue, field);
      });

      if (usecase === "multi_invoice") {
        const days = differenceInCalendarDays(
          jsonLoanRequest.ending_date,
          jsonLoanRequest.starting_date,
        );
        setDaysSelect(
          daysSelectOptions.find((option) => option.value === days),
        );
      }
    }

    setPage(1);
  };

  const init = async () => {
    setLoading(true);
    await initCreditLine();
    await initBankAccounts();
    await initCurrencies();
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    setLoadingData(true);
    if (loanRequest.id && !loading && !loadingActiveCompany) initData();
    setLoadingData(false);
  }, [loading, loadingActiveCompany]);

  useEffect(() => {
    if (!loanRequest.id) {
      setPage(1);
    }
  }, [loanRequest.id]);

  if (!page || loading || loadingActiveCompany || loadingData) return <></>;

  if (page === 2 && loanRequest.invoices.length > 0)
    return (
      <Invoices
        loanRequestID={loanRequest.id}
        onBack={() => init()}
        onEstimate={() => onEstimate(loanRequest.id)}
        usecase={usecase}
        need={need}
      />
    );

  return (
    <Main themeSwitch>
      <Header>
        <Typo variant="h1">{t("financing:fillout.loanRequest.heading")}</Typo>
        <Typo variant="body2">
          {t("financing:fillout.loanRequest.subheading")}
        </Typo>
      </Header>
      <Content
        style={{
          display: "flex",
          gap: "4rem",
          flexWrap: "wrap-reverse",
          justifyContent: creditLine ? "center" : "flex-start",
        }}
      >
        <div style={{ flexBasis: "50rem", flexGrow: creditLine ? "1" : "0" }}>
          <Form {...filterForm()} errors={loanRequestErrors.non_field_errors} />
        </div>
        {creditLine && (
          <div>
            <CreditLineGauge {...creditLine} />
          </div>
        )}
      </Content>
    </Main>
  );
}

function Invoices({ loanRequestID, onBack, onEstimate, usecase, need }) {
  const { t } = useTranslation(["common", "financing"]);
  const dispatchModal = useModalDispatch();

  const today = () => new Date().setHours(0, 0, 0, 0);

  const [invoices, setInvoices] = useState([]);
  const [selectedID, setSelectedID] = useState("");
  const [loading, setLoading] = useState(true);

  const computeTotalAmount = (invoices) =>
    invoices
      .map((invoice) =>
        invoice.amount_with_tax ? parseFloat(invoice.amount_with_tax) : 0,
      )
      .reduce((prev, current) => prev + current, 0);

  const [totalTTCAmount, setTotalTTCAmount] = useState(
    invoices ? computeTotalAmount(invoices) : 0,
  );

  useEffect(() => {
    if (invoices) setTotalTTCAmount(computeTotalAmount(invoices));
  }, [invoices]);

  // const handleInvoicesChange = (id, newValue) => {
  //   setInvoices((prev) => [
  //     ...prev.filter((invoice) => invoice.id !== id),
  //     {
  //       ...prev.find((invoice) => invoice.id === id),
  //       ...newValue,
  //     },
  //   ]);
  // };

  const isInvoiceExpired = (invoice) =>
    invoice.due_date
      ? differenceInCalendarDays(invoice.due_date, today()) < 0
      : false;

  const init = async () => {
    setLoading(true);
    setSelectedID("");

    const loanRequestResponse = await api.getLoanRequest(loanRequestID);
    if (loanRequestResponse.ok) {
      const jsonLoanRequest = await loanRequestResponse.json();
      const invoiceIDs = jsonLoanRequest.invoices;
      let loadedInvoices = [];

      const counterpartiesResponse = await api.getCounterparties(true);
      if (counterpartiesResponse.ok) {
        const jsonCounterparties = await counterpartiesResponse.json();

        for (const id of invoiceIDs) {
          const invoiceResponse = await api.getInvoice(id);
          if (invoiceResponse.ok) {
            const jsonInvoice = await invoiceResponse.json();
            loadedInvoices.push({
              id: id,
              ...jsonInvoice,
              expired: isInvoiceExpired(jsonInvoice),
              complete: useInvoiceCheck(jsonInvoice),
              counterparty: jsonInvoice.counterparty
                ? jsonCounterparties.find(
                    (cp) => cp.id === jsonInvoice.counterparty,
                  ).label
                : jsonInvoice.counterparty,
            });
          }
        }

        setInvoices(loadedInvoices);
        setSelectedID(
          usecase === "multi_invoice"
            ? ""
            : loadedInvoices.length > 0 && loadedInvoices[0].id,
        );
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleSubmit = async () => {
    const patchResponse = await api.patchLoanRequest(loanRequestID, {
      amount: totalTTCAmount,
    });
    if (patchResponse.ok) onEstimate();
  };

  const handleSelect = (id) => setSelectedID(id);

  const handleDelete = async (id) => {
    const handleDelete = async () => {
      const response = await api.deleteInvoice(id);
      if (response.ok) {
        dispatchModal({ type: "success" });
      }
    };

    const modal = {
      title: t("financing:fillout.invoices.modals.delete.title"),
      message: t("financing:fillout.invoices.modals.delete.message"),
      component: <Form buttonVariant="error" onSubmit={handleDelete} />,
      onSuccess: init,
    };

    dispatchModal({ type: "add", ...modal });
  };

  const mainButtonProps = (invoice) => ({
    variant: invoice.complete ? null : "primary_a",
    text: invoice.complete ? null : t("common:addInfo"),
    onClick: () => handleSelect(invoice.id),
    icon: invoice.complete ? <Edit /> : null,
    title: invoice.complete ? t("common:modify") : null,
  });

  const handleImport = () => {
    const modal = {
      title: t("common:invoice"),
      component: <InvoiceImport loanRequestID={loanRequestID} need={need} />,
      onSuccess: init,
    };
    dispatchModal({ type: "add", ...modal });
  };

  if (loading) return <FormLoader />;
  if (selectedID) {
    return (
      <InvoiceForm
        invoiceID={selectedID}
        onBack={
          usecase !== "multi_invoice" ? () => onBack() : () => setSelectedID("")
        }
        onPatchSuccess={usecase === "multi_invoice" ? init : onEstimate}
        usecase={usecase}
      />
    );
  }

  return (
    <Main themeSwitch>
      <Header onBackButtonClick={onBack}>
        <Typo variant="h1">{t("financing:fillout.invoices.heading")}</Typo>
      </Header>
      {loading ? (
        <FormLoader />
      ) : (
        <Content
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          <div style={{ marginLeft: "auto" }}>
            <Button
              variant={"secondary_b"}
              text={t("financing:fillout.invoices.addButton")}
              onClick={handleImport}
            />
          </div>

          {/* Desktop */}
          <div className="InvoiceTable">
            {invoices.length > 0 &&
              invoices.map((invoice) => (
                <div className="Invoice" key={invoice.id}>
                  <div className="Invoice-items">
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Description />
                      <span
                        title={
                          invoice.reference || `[${t("common:reference")}]`
                        }
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {invoice.reference || `[${t("common:reference")}]`}
                      </span>
                    </span>
                    <span
                      title={invoice.counterparty && invoice.counterparty}
                      className="Invoice-item_wide"
                    >
                      {invoice.counterparty || `[${t("common:counterparty")}]`}
                    </span>
                    <span>
                      {invoice.amount_with_tax
                        ? invoice.amount_with_tax &&
                          formatAmount(
                            invoice.amount_with_tax,
                            i18n.resolvedLanguage,
                            invoice.currency,
                          )
                        : `[${t("common:amount")}]`}
                    </span>
                    {invoice.expired ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          color: "var(--color-warning-1)",
                        }}
                      >
                        <InfoOutlined />
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {t("common:pastDueInvoice")}
                        </span>
                      </span>
                    ) : (
                      <div></div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "2rem",
                      alignItems: "center",
                    }}
                  >
                    <Button {...mainButtonProps(invoice)} />
                    {invoices.length > 1 && (
                      <Button
                        icon={<Delete />}
                        title={t("common:delete")}
                        onClick={() => handleDelete(invoice.id)}
                      />
                    )}
                  </div>
                </div>
              ))}
          </div>

          {/* Mobile */}
          <InvoiceCards>
            {invoices.length > 0 &&
              invoices.map((invoice) => (
                <InvoiceCard key={invoice.id}>
                  <InvoiceCardHeader>
                    <Description />
                    <span
                      title={invoice.reference || `[${t("common:reference")}]`}
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {invoice.reference || `[${t("common:reference")}]`}
                    </span>
                  </InvoiceCardHeader>

                  <InvoiceCardContent>
                    <InvoiceCardItems>
                      <Typo
                        title={invoice.counterparty && invoice.counterparty}
                        className="Invoice-item_wide"
                      >
                        {invoice.counterparty ||
                          `[${t("common:counterparty")}]`}
                      </Typo>
                      <Typo>
                        {invoice.amount_with_tax
                          ? invoice.amount_with_tax &&
                            formatAmount(
                              invoice.amount_with_tax,
                              i18n.resolvedLanguage,
                              invoice.currency,
                            )
                          : `[${t("common:amount")}]`}
                      </Typo>
                      {invoice.expired && (
                        <Typo
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            color: "var(--color-warning-1)",
                          }}
                        >
                          <InfoOutlined />
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {t("common:pastDueInvoice")}
                          </span>
                        </Typo>
                      )}
                    </InvoiceCardItems>
                    <InvoiceCardButtons>
                      {" "}
                      <Button {...mainButtonProps(invoice)} />
                      {invoices.length > 1 && (
                        <Button
                          icon={<Delete />}
                          title={t("common:delete")}
                          onClick={() => handleDelete(invoice.id)}
                        />
                      )}
                    </InvoiceCardButtons>
                  </InvoiceCardContent>
                </InvoiceCard>
              ))}
          </InvoiceCards>

          {invoices.length > 0 && (
            <div style={{ justifySelf: "right" }}>
              <Amount
                label={t("common:totalAmount")}
                amount={formatAmount(
                  totalTTCAmount,
                  i18n.resolvedLanguage,
                  invoices[0].currency,
                )}
                warning={invoices.some((invoice) => invoice.expired)}
                warningPhrase={t(
                  "financing:fillout.invoices.pastDueInvoiceWarning",
                )}
              />
            </div>
          )}
          <div>
            <Button
              variant={"primary_a"}
              text={t("common:confirm")}
              onClick={handleSubmit}
              disabled={
                invoices.some((invoice) => invoice.expired) ||
                invoices.some((invoice) => !invoice.complete)
              }
            />
          </div>
        </Content>
      )}
    </Main>
  );
}

function InvoiceCards({ children }) {
  return <div className="InvoiceCards"> {children}</div>;
}

function InvoiceCard({ children }) {
  return <div className="InvoiceCard">{children}</div>;
}

function InvoiceCardHeader({ children }) {
  return <div className="InvoiceCardHeader">{children}</div>;
}

function InvoiceCardContent({ children }) {
  return <div className="InvoiceCardContent">{children}</div>;
}

function InvoiceCardItems({ children }) {
  return <div className="InvoiceCardItems">{children}</div>;
}

function InvoiceCardButtons({ children }) {
  return <div className="InvoiceCardButtons">{children}</div>;
}

function InvoiceImport({ loanRequestID, need }) {
  const { t } = useTranslation(["common", "financing"]);
  const dispatchModal = useModalDispatch();

  const [invoiceType] = useState(useInvoiceType(need));
  const [invoiceFiles, setInvoiceFiles] = useState([]);

  const inputs = [
    {
      data: [
        {
          type: "file",
          value: invoiceFiles,
          onChange: setInvoiceFiles,
        },
      ],
    },
  ];

  const handleSubmit = async () => {
    const newInvoice = new FormData();
    newInvoice.append("file", invoiceFiles[0]);
    newInvoice.append("type", invoiceType.value);
    const invoiceResponse = await api.createInvoice(newInvoice);
    if (invoiceResponse.ok) {
      const jsonInvoice = await invoiceResponse.json();
      const ogLoanRequestResponse = await api.getLoanRequest(loanRequestID);
      if (ogLoanRequestResponse.ok) {
        const ogJsonLoanRequest = await ogLoanRequestResponse.json();
        const loanRequestResponse = await api.patchLoanRequest(loanRequestID, {
          invoices: [...ogJsonLoanRequest.invoices, jsonInvoice.id],
        });
        if (loanRequestResponse.ok) {
          dispatchModal({ type: "success" });
        }
      }
    }
  };

  const form = {
    inputs: inputs,
    button: t("vault:common.fileDrop.upload"),
    onSubmit: handleSubmit,
    isButtonDisabled: invoiceFiles.length === 0,
    className: "FileDropForm",
  };
  return <Form {...form} />;
}

function InvoiceForm({ invoiceID, onBack, onPatchSuccess, usecase }) {
  const { t } = useTranslation(["common", "financing"]);
  document.title = t("financing:fillout.title");

  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const { activeCompany, loadingActiveCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingResources, setLoadingResources] = useState(true);
  const [counterparties, setCounterparties] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([
    "FR",
    "IT",
    "DE",
  ]);
  const [companyCodes, setCompanyCodes] = useState([
    {
      FR: "123456789",
    },
  ]);
  const [invoice, setInvoice] = useState({
    id: 0,
    reference: "",
    creation_date: "",
    amount_without_tax: "",
    amount_with_tax: "",
    currency: currencies.find(
      (currency) => currency.value === activeCompany.currency,
    ),
    due_date: "",
    counterparty: null,
    file: null, // string url
  });

  const handleInvoiceChange = (newValue, field) => {
    setInvoice((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const initCounterparties = async () => {
    handleInvoiceChange(null, "counterparty");
    const response = await api.getCounterparties(true);
    if (response.ok) {
      const allCounterparties = await response.json();
      const options = allCounterparties.map((counterparty) => ({
        label: counterparty.label,
        value: counterparty.id,
      }));
      setCounterparties(options);
    }
  };

  const initCountries = async () => {
    const response = await api.getCountries();
    if (response.ok) {
      const jsonResponse = await response.json();
      setAvailableCountries(jsonResponse);
    }
  };

  const initCompanyCodes = async () => {
    const response = await api.getCompanyCodes();
    if (response.ok) {
      const jsonResponse = await response.json();
      setCompanyCodes(jsonResponse);
    }
  };

  const initCurrencies = async () => {
    const response = await api.getCurrencies();
    if (response.ok) {
      const jsonCurrencies = await response.json();
      const currenciesOptions = jsonCurrencies.map((currency) => ({
        label: currency,
        value: currency,
      }));
      setCurrencies(currenciesOptions);
    }
  };

  const initInvoiceData = async () => {
    if (invoiceID) {
      const invoiceResponse = await api.getInvoice(invoiceID);
      if (invoiceResponse.ok) {
        const jsonInvoice = await invoiceResponse.json();
        let loadedInvoice = {
          ...jsonInvoice,
          id: invoiceID,
          currency: currencies.find(
            (currency) => currency.value === jsonInvoice.currency,
          ),
          counterparty: counterparties.find(
            (counterparty) => counterparty.value === jsonInvoice.counterparty,
          ),
        };

        Object.entries(loadedInvoice).forEach((entry) => {
          const field = entry[0];
          const newValue = entry[1];
          handleInvoiceChange(newValue, field);
        });
      }
    }
  };

  const initResources = async () => {
    setLoadingResources(true);
    await initCounterparties();
    await initCountries();
    await initCompanyCodes();
    await initCurrencies();
    setLoadingResources(false);
  };

  const init = async () => {
    setLoading(true);
    await initInvoiceData();
    setLoading(false);
  };

  useEffect(() => {
    initResources();
  }, []);

  useEffect(() => {
    if (!loadingResources) init();
  }, [loadingResources]);

  const initialInvoiceErrors = {
    reference: [],
    creation_date: [],
    amount_without_tax: [],
    amount_with_tax: [],
    currency: [],
    due_date: [],
    counterparty: [],
    non_field_errors: [],
  };

  const [invoiceErrors, setInvoiceErrors] = useState(initialInvoiceErrors);

  const handleCreateCounterparty = () => {
    const handleCreatedCounterparty = (newCounterparty) => {
      initResources();
      handleInvoiceChange(newCounterparty, "counterparty");
      dispatchToast({
        type: "add",
        variant: "success",
        subheading: t(
          "financing:fillout.invoice.toasts.counterparty.subheading",
        ),
      });
    };

    const modal = {
      title: t("financing:fillout.invoice.modals.counterparty.title"),
      message: t("financing:fillout.invoice.modals.counterparty.message"),
      component: (
        <NewCounterparty
          onCreate={handleCreatedCounterparty}
          availableCountries={availableCountries}
          companyCodes={companyCodes}
        />
      ),
      size: "medium",
    };

    dispatchModal({
      type: "add",
      ...modal,
    });
  };

  const counterpartyInput = {
    type: "select",
    name: "counterparty",
    label: t("common:counterparty"),
    value: invoice.counterparty,
    onChange: (value) => handleInvoiceChange(value, "counterparty"),
    options: counterparties,
    errors: invoiceErrors.counterparty,
    isSearchable: true,
    noOptionsMessage: (
      <Button
        style={{
          marginLeft: "auto",
          padding: "0.5rem 0",
          fontSize: "unset",
          fontWeight: "500",
          color: "var(--color-leano-2) !important",
        }}
        onClick={() => handleCreateCounterparty()}
        text={t(
          "financing:fillout.invoice.form.inputs.counterparty.noOptionsMessage",
        )}
        icon={
          <AddBox
            style={{ fontSize: "large" }}
            sx={{ color: "var(--color-leano-2) !important" }}
          />
        }
        iconLeft
      />
    ),
    placeholder: t(
      "financing:fillout.invoice.form.inputs.counterparty.placeholder",
    ),
  };

  const invoiceRefInput = {
    type: "text",
    name: "invoiceRef",
    label: t("common:reference"),
    value: invoice.reference,
    onChange: (value) => handleInvoiceChange(value, "reference"),
    errors: invoiceErrors.reference,
  };

  const invoiceCreationDateInput = {
    type: "date",
    name: "invoiceCreationDate",
    label: t("common:creationDate"),
    value: invoice.creation_date,
    onChange: (value) => handleInvoiceChange(value, "creation_date"),
    errors: invoiceErrors.creation_date,
  };

  const invoiceAmountHTInput = {
    type: "number",
    name: "invoiceAmountHT",
    label: t("common:amountHT"),
    value: invoice.amount_without_tax,
    onChange: (value) => handleInvoiceChange(value, "amount_without_tax"),
    errors: invoiceErrors.amount_without_tax,
    min: 0,
    onFormatNumber: (value) =>
      formatAmount(
        value,
        i18n.resolvedLanguage,
        invoice.currency && invoice.currency.value,
        false,
        false,
      ),
  };

  const invoiceAmountTTCInput = {
    type: "number",
    name: "invoiceAmountTTC",
    label: t("common:amountTTC"),
    value: invoice.amount_with_tax,
    onChange: (value) => handleInvoiceChange(value, "amount_with_tax"),
    errors: invoiceErrors.amount_with_tax,
    min: 0,
    onFormatNumber: (value) =>
      formatAmount(
        value,
        i18n.resolvedLanguage,
        invoice.currency && invoice.currency.value,
        false,
        false,
      ),
  };

  const invoiceCurrencyInput = {
    type: "select",
    name: "invoiceCurrency",
    label: t("common:currency"),
    value: invoice.currency,
    onChange: (value) => handleInvoiceChange(value, "currency"),
    options: currencies,
    errors: invoiceErrors.currency,
  };

  const invoiceDueDateInput = {
    type: "date",
    name: "invoiceDueDate",
    label: t("common:dueDate"),
    value: invoice.due_date,
    onChange: (value) => handleInvoiceChange(value, "due_date"),
    errors: invoiceErrors.due_date,
  };

  const defaultInputs = [
    {
      data: [
        counterpartyInput,
        invoiceRefInput,
        invoiceCreationDateInput,
        invoiceAmountHTInput,
        invoiceAmountTTCInput,
        invoiceCurrencyInput,
        invoiceDueDateInput,
      ],
    },
  ];

  const multiInvoiceInputs = [
    {
      data: [
        counterpartyInput,
        invoiceRefInput,
        invoiceCreationDateInput,
        invoiceAmountHTInput,
        invoiceAmountTTCInput,
        invoiceDueDateInput,
      ],
    },
  ];

  const clearErrors = () => {
    setInvoiceErrors(initialInvoiceErrors);
  };

  const handleErrorsChange = (newErrors = {}) => {
    setInvoiceErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));
  };

  const handlePatchInvoice = async () => {
    clearErrors();

    let patchedInvoice = {
      ...invoice,
      currency: invoice.currency.value,
      counterparty: invoice.counterparty.value,
    };
    delete patchedInvoice.id;
    delete patchedInvoice.file;
    const response = await api.patchInvoice(invoice.id, patchedInvoice);

    if (response.ok) {
      onPatchSuccess(invoice);
    } else {
      const jsonErrors = await response.json();
      handleErrorsChange(jsonErrors);
    }
  };

  return (
    <Main themeSwitch>
      <Header onBackButtonClick={onBack}>
        {(!loading && invoice.reference) || (
          <Typo variant="h1">{t("financing:fillout.invoice.heading")}</Typo>
        )}
      </Header>
      {loading || loadingActiveCompany || loadingResources ? (
        <FormLoader />
      ) : (
        <Content
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4rem",
          }}
        >
          <iframe
            style={{
              flexBasis: "30rem",
              flexGrow: "1",
              border: "none",
              boxShadow: "0px 4px 4px 0px var(--color-neutral-2)",
              borderRadius: "0.65rem",
              height: "70svh",
            }}
            src={invoice.file}
          />
          <div style={{ flexBasis: "30rem" }}>
            <Form
              inputs={
                usecase !== "multi_invoice" ? defaultInputs : multiInvoiceInputs
              }
              onSubmit={handlePatchInvoice}
              button={t("financing:fillout.invoice.form.button")}
              errors={invoiceErrors.non_field_errors}
            />
          </div>
        </Content>
      )}
    </Main>
  );
}

function NewCounterparty({ onCreate, availableCountries, companyCodes }) {
  const { t, i18n } = useTranslation("common", "financing");
  const countries = useCountries();
  const dispatchModal = useModalDispatch();

  const [counterparty, setCounterparty] = useState({
    name: "",
    country: null,
    identifier: "",
  });

  const initialErrors = {
    name: [],
    country: [],
    identifier: [],
    non_field_errors: [],
  };

  const [counterpartyErrors, setCounterpartyErrors] = useState(initialErrors);

  const handleCounterpartyChange = (newValue, field) => {
    setCounterparty((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const nameInput = {
    type: "text",
    name: "name",
    label: t("common:name"),
    value: counterparty.name,
    onChange: (value) => handleCounterpartyChange(value, "name"),
    errors: counterpartyErrors.name,
  };

  const countryInput = {
    type: "select",
    name: "country",
    label: t("common:country"),
    value: counterparty.country,
    onChange: (value) => handleCounterpartyChange(value, "country"),
    options: Object.entries(
      countries.getNames(i18n.resolvedLanguage, { select: "official" }),
    )
      .map(([alpha2Code, name]) => {
        return {
          label: name,
          value: alpha2Code,
        };
      })
      .filter((country) =>
        availableCountries.some(
          (availableCountry) => availableCountry === country.value,
        ),
      ),
    isSearchable: true,
    errors: counterpartyErrors.country,
  };

  const IDInput = {
    type: "text",
    name: "identifier",
    placeholder:
      counterparty.country && companyCodes[counterparty.country.value],
    label: t("common:identifier"),
    value: counterparty.identifier,
    onChange: (value) => handleCounterpartyChange(value, "identifier"),
    errors: counterpartyErrors.identifier,
  };

  const inputs = [
    {
      data: [nameInput],
    },
    {
      data: [countryInput],
    },
    {
      data: [IDInput],
    },
  ];

  const handleSubmit = async () => {
    setCounterpartyErrors(initialErrors);
    const createdResponse = await api.createCounterparty({
      ...counterparty,
      country: counterparty.country.value,
    });

    if (createdResponse.ok) {
      const createdCounterparty = await createdResponse.json();
      const newCounterpartiesResponse = await api.getCounterparties(true);

      if (newCounterpartiesResponse.ok) {
        const newCounterparties = await newCounterpartiesResponse.json();

        const newCounterparty_formatted = newCounterparties.filter(
          (counterparty) => counterparty.id === createdCounterparty.id,
        )[0];

        dispatchModal({
          type: "success",
          onSuccess: () =>
            onCreate({
              label: newCounterparty_formatted.label,
              value: newCounterparty_formatted.id,
            }),
        });
      }
    } else {
      const jsonErrors = await createdResponse.json();
      setCounterpartyErrors(jsonErrors);
    }
  };

  return (
    <Form
      inputs={inputs}
      onSubmit={handleSubmit}
      button={t("financing:fillout.invoice.modals.counterparty.button")}
      errors={counterpartyErrors.non_field_errors}
    />
  );
}
